import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {setHoveredSprite} from '../reducers/hovered-target';
import {updateAssetDrag} from '../reducers/asset-drag';
import storage from '../lib/storage';
import {getEventXY} from '../lib/touch-utils';
import {getHomeworkInfo} from '../lib/homework-info';

import SpriteSelectorItemComponent from '../components/sprite-selector-item/sprite-selector-item.jsx';
import {injectIntl, intlShape, defineMessages} from 'react-intl';
import {VM} from '@hetao/scratch-core';
import {ScratchSVGRender} from '@hetao/scratch-core';
import getCostumeUrl from '../lib/get-costume-url';

const { SVGRenderer } = ScratchSVGRender;
const dragThreshold = 3; // Same as the block drag threshold
const HAS_FONT_REGEXP = 'font-family(?!="none")';

const messages = defineMessages({
    deleteAlert: {
        defaultMessage: 'Are you sure you want to delete this?',
        description: 'delete sprite alert',
        id: 'gui.assetItem.deleteAlert'
    }
});

class SpriteSelectorItem extends React.Component {

    static getDerivedStateFromProps (props, state) {
        if (state.asset !== props.asset) {
            state.asset = props.asset;
            state.imgCommit = true;
        }
        return state;
    }

    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeSpriteName',
            'handleClick',
            // 'getCostumeUrl',
            'handleConfig',
            'handleDelete',
            'handleClickLock',
            'handleClickNotLock',
            'handleDisabled',
            'handleDuplicate',
            'handleExitEditMode',
            'handleIntoEditMode',
            'handleExport',
            'handleMouseEnter',
            'handleMouseLeave',
            'handleMouseDown',
            'handleMouseMove',
            'handleMouseUp',
            'getCostumeData',
            'handleDisplaySprite'
        ]);
        this.svgRenderer = new SVGRenderer();
        // Asset ID of the SVG currently in SVGRenderer
        this.decodedAssetId = null;
        this.state = {
            img: this.props.costumeURL,
            imgCommit: false,
            editMode: false,
            isDragging: false,
            asset: this.props.asset
        };
    }
    componentDidMount () {
        this.homeworkInfo = getHomeworkInfo();
        this.courseId = Number(this.homeworkInfo.courseId);
        this.unitId = Number(this.homeworkInfo.unitId);
        this.lessonId = Number(this.homeworkInfo.lessonId);
        this.getImg();
    }
    shouldComponentUpdate (nextProps, nextState) {
        // Ignore dragPayload due to https://github.com/LLK/scratch-gui/issues/3172.
        // This function should be removed once the issue is fixed.
        for (const property in nextProps) {
            if (property !== 'dragPayload' && this.props[property] !== nextProps[property]) {
                return true;
            }
        }
        for (const property in nextState) {
            if (this.state[property] !== nextState[property]) {
                return true;
            }
        }
        return false;
    }
    componentDidUpdate () {
        if (this.state.imgCommit) {
            this.getImg();
            // eslint-disable-next-line
            this.setState({
                imgCommit: false
            });
        }
    }

    getImg () {
        if (!this.props.asset) return;
        getCostumeUrl(this.props.asset).then(img => {
            this.setState({
                img
            });
        });
    }

    getCostumeData () {
        return this.state.img;
    }

    handleChangeSpriteName (name) {
        const id = this.props.id;
        const nameOld = this.props.name;
        ht.track('s3_sprite_rename', {
            context: {
                courseId: this.courseId,
                unitId: this.unitId,
                lessonId: this.lessonId,
                currentUrl: location.href,
                stageDisplayMode: localStorage.getItem('stageDisplayMode')
            },
            param: {
                sprite: {
                    id: id,
                    name: nameOld
                }
            }
        });
        this.props.vm.renameSprite(this.props.editingTarget, name);
    }
    handleIntoEditMode () {
        this.setState({
            editMode: true
        });
    }

    handleExitEditMode () {
        this.setState({
            editMode: false
        });
    }
    handleMouseUp () {
        this.initialOffset = null;
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('touchend', this.handleMouseUp);
        window.removeEventListener('touchmove', this.handleMouseMove);
        const id = this.props.id;
        const name = this.props.name;
        ht.track('s3_sprite_drag', {
            context: {
                courseId: this.courseId,
                unitId: this.unitId,
                lessonId: this.lessonId,
                currentUrl: location.href,
                stageDisplayMode: localStorage.getItem('stageDisplayMode')
            },
            param: {
                sprite: {
                    id: id,
                    name: name
                }
            }
        });
        this.props.onDrag({
            img: null,
            currentOffset: null,
            dragging: false,
            dragType: null,
            index: null
        });
        setTimeout(() => {
            this.noClick = false;
        });
    }

    handleMouseMove (e) {
        const currentOffset = getEventXY(e);
        const dx = currentOffset.x - this.initialOffset.x;
        const dy = currentOffset.y - this.initialOffset.y;
        if (Math.sqrt((dx * dx) + (dy * dy)) > dragThreshold) {
            this.props.onDrag({
                img: this.getCostumeData(),
                currentOffset: currentOffset,
                dragging: true,
                dragType: this.props.dragType,
                index: this.props.index,
                payload: this.props.dragPayload
            });
            this.noClick = true;
        }
        e.preventDefault();
    }
    handleMouseDown (e) {
        this.initialOffset = getEventXY(e);
        window.addEventListener('mouseup', this.handleMouseUp);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('touchend', this.handleMouseUp);
        window.addEventListener('touchmove', this.handleMouseMove);
    }
    handleClick (e) {
        e.preventDefault();
        const id = this.props.id;
        const name = this.props.name;
        const category = this.props.isStage ? 'stage' : 'sprite'; //eslint-disable-line
        const param = this.props.isStage ? {stage: {id: id, name: name}} : {sprite: {id: id, name: name}};
        ht.track(`s3_${category}_select`, {
            context: {
                courseId: this.courseId,
                unitId: this.unitId,
                lessonId: this.lessonId,
                currentUrl: location.href,
                stageDisplayMode: localStorage.getItem('stageDisplayMode')
            },
            param: param
        });
        if (!this.noClick) {
            this.props.onClick(this.props.id);
        }
    }
    handleConfig (e) {
        const id = this.props.id;
        const name = this.props.name;
        const category = this.props.isStage ? 'stage' : 'sprite'; //eslint-disable-line
        const param = this.props.isStage ? {stage: {id: id, name: name}} : {sprite: {id: id, name: name}};
        ht.track(`s3_${category}_config`, {
            context: {
                courseId: this.courseId,
                unitId: this.unitId,
                lessonId: this.lessonId,
                currentUrl: location.href,
                stageDisplayMode: localStorage.getItem('stageDisplayMode')
            },
            param: param

        });
        e.stopPropagation();
        this.props.onConfigClick(this.props.id);
    }
    handleDelete (e) {
        if (this.handleDisabled()) {
            return;
        }
        e.stopPropagation(); // To prevent from bubbling back to handleClick
        const msg = this.props.intl.formatMessage(messages.deleteAlert);
        // eslint-disable-next-line no-alert
        if (window.confirm(msg)) {
            const id = this.props.id;
            const name = this.props.name;
            ht.track('s3_sprite_remove', {
                context: {
                    courseId: this.courseId,
                    unitId: this.unitId,
                    lessonId: this.lessonId,
                    currentUrl: location.href,
                    stageDisplayMode: localStorage.getItem('stageDisplayMode')
                },
                param: {
                    sprite: {
                        id: id,
                        name: name
                    }
                }
            });
            this.props.onDeleteButtonClick(this.props.id);
        }
    }
    handleDuplicate (e) {
        if (this.handleDisabled()) {
            return;
        }
        e.stopPropagation(); // To prevent from bubbling back to handleClick
        this.props.onDuplicateButtonClick(this.props.id);
    }
    handleExport (e) {
        e.stopPropagation();
        this.props.onExportButtonClick(this.props.id);
    }
    handleMouseLeave () {
        this.props.dispatchSetHoveredSprite(null);
    }
    handleMouseEnter () {
        this.props.dispatchSetHoveredSprite(this.props.id);
    }
    handleClickLock (e) {
        e.preventDefault();
        this.props.vm.setDisabled(true);
    }
    handleClickNotLock (e) {
        e.preventDefault();
        this.props.vm.setDisabled(false);
    }
    handleDisabled () {
        if (this.props.disabled) {
            alert('不要改动老师已经写好的角色，会影响你正常过关'); // eslint-disable-line
            return true;
        }
        return false;
    }
    handleDisplaySprite (isShow) {
        if (isShow) {
            this.props.vm.setTargetStatus('HIDE');
        } else {
            this.props.vm.setTargetStatus('SHOW');
        }
    }
    render () {
        const {
            /* eslint-disable no-unused-vars */
            assetId,
            id,
            index,
            onClick,
            onConfigClick,
            onDeleteButtonClick,
            onDuplicateButtonClick,
            onExportButtonClick,
            dragPayload,
            receivedBlocks,
            costumeURL,
            vm,
            /* eslint-enable no-unused-vars */
            ...props
        } = this.props;
        return (
            <SpriteSelectorItemComponent
                costumeURL={this.getCostumeData()}
                onHandleDisplaySprite={this.handleDisplaySprite}
                editMode={this.state.editMode}
                onChangeName={this.handleChangeSpriteName}
                onClick={this.handleClick}
                onClickLock={this.handleClickLock}
                onClickNotLock={this.handleClickNotLock}
                onConfigButtonClick={onConfigClick ? this.handleConfig : null}
                onDeleteButtonClick={onDeleteButtonClick ? this.handleDelete : null}
                onDuplicateButtonClick={onDuplicateButtonClick ? this.handleDuplicate : null}
                onExitEditMode={this.handleExitEditMode}
                onExportButtonClick={onExportButtonClick ? this.handleExport : null}
                onIntoEditMode={this.handleIntoEditMode}
                onMouseDown={this.handleMouseDown}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                {...props}
            />
        );
    }
}

SpriteSelectorItem.propTypes = {
    asset: PropTypes.instanceOf(storage.Asset),
    assetId: PropTypes.string,
    costumeURL: PropTypes.string,
    disabled: PropTypes.bool,
    dispatchSetHoveredSprite: PropTypes.func.isRequired,
    dragPayload: PropTypes.shape({
        isStage: PropTypes.bool,
        name: PropTypes.string,
        body: PropTypes.string
    }),
    dragType: PropTypes.string,
    editingTarget: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    index: PropTypes.number,
    intl: intlShape.isRequired,
    isSprite: PropTypes.bool,
    isStage: PropTypes.bool,
    name: PropTypes.string,
    onClick: PropTypes.func,
    onConfigClick: PropTypes.func,
    onDeleteButtonClick: PropTypes.func,
    onDrag: PropTypes.func.isRequired,
    onDuplicateButtonClick: PropTypes.func,
    onExportButtonClick: PropTypes.func,
    receivedBlocks: PropTypes.bool.isRequired,
    selected: PropTypes.bool,
    vm: PropTypes.instanceOf(VM)
};

const mapStateToProps = (state, {id}) => ({
    // costumeURL: costumeURL || (assetId && state.scratchGui.vm.runtime.storage.get(assetId).encodeDataURI()),
    dragging: state.scratchGui.assetDrag.dragging,
    editingTarget: state.scratchGui.targets.editingTarget,
    receivedBlocks: state.scratchGui.hoveredTarget.receivedBlocks &&
            state.scratchGui.hoveredTarget.sprite === id,
    vm: state.scratchGui.vm
});
const mapDispatchToProps = dispatch => ({
    dispatchSetHoveredSprite: spriteId => {
        dispatch(setHoveredSprite(spriteId));
    },
    onDrag: data => dispatch(updateAssetDrag(data))
});

const ConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(SpriteSelectorItem));

export {
    ConnectedComponent as default,
    HAS_FONT_REGEXP // Exposed for testing
};
